// NPM Modules
import dayjs from 'dayjs';
// Models
import Model from './BaseModel.js';
import Race from './Race.js';
import Sport from './Sport.js';
import Meeting from './Meeting.js';
import Selection from './Selection.js';
import SportSelection from './SportSelection.js';
import Competition from '@/Models/Competition.js';
// Constants
import { AlertLevel, AlertTypes } from '@/Constants/Alert.js';
import { BetCategory } from '@/Constants/Bet.js';
// Services
import { getIconForSport, routeForMeeting, routeForMeetingRace, routeForSportEvent } from '@/Services/Helpers.js';
// Models
import SportcastSelection from '@/Models/SportcastSelection.js';
import ACLStore from '@/Stores/ACLStore.js';

export default class Alert extends Model {
	resourceName() {
		return 'alerts';
	}

	fields() {
		return ['alert_type', 'created_at', 'dismissed'];
	}

	relationships() {
		return {
			source: (relationship) => {
				if (relationship === 'races') {
					return new Race();
				}
				if (relationship === 'sports') {
					return new Sport();
				}
				if (relationship === 'meetings') {
					return new Meeting();
				}
				if (relationship === 'Sport') {
					return new SportSelection();
				}
				if (relationship === 'Racing') {
					return new Selection();
				}
				if (relationship === 'sportcast_selections') {
					return new SportcastSelection();
				}
			}
		};
	}

	getCurrentAlertLevel() {
		const createdAt = dayjs(this.created_at);
		const diffInMinutes = dayjs().diff(createdAt, 'minute', true);
		if (diffInMinutes >= 20) {
			return AlertLevel.Danger;
		} else if (diffInMinutes >= 10 && diffInMinutes < 20) {
			return AlertLevel.Warning;
		}
		return AlertLevel.None;
	}

	computed() {
		//These are static after the model is created
		return {
			title(alert) {
				if (alert.alert_type.id === AlertTypes.RESULT_MISMATCH) {
					if (alert.source instanceof Race) {
						return 'Mismatching results received for racing event';
					} else if (alert.source instanceof Meeting) {
						return 'Mismatching results received for racing meeting';
					}
				} else if (alert.alert_type.id === AlertTypes.NOT_FINALISED) {
					if (alert.source instanceof Race) {
						return 'Racing event has not been finalised';
					} else if (alert.source instanceof Meeting) {
						return 'Racing meeting has not been finalised';
					}
				} else if (alert.alert_type.id === AlertTypes.SELECTION_LIABILITY_EXCEEDED) {
					if (alert.source instanceof Selection) {
						return 'Racing selection has exceeded liability limit';
					} else if (alert.source instanceof SportSelection) {
						return 'Sport selection has exceeded liability limit';
					}
				} else if (alert.alert_type.id === AlertTypes.MULTI_SELECTIONS_LIABILITY_EXCEEDED) {
					if (alert.source instanceof Selection) {
						return 'Racing selection has exceeded multi liability limit';
					} else if (alert.source instanceof SportSelection) {
						return 'Sport selection has exceeded multi liability limit';
					}
				} else if (alert.alert_type.id === AlertTypes.SGM_SELECTION_LIABILITY_EXCEEDED) {
					return 'SGM selection has exceeded liability limit';
				} else if (alert.alert_type.id === AlertTypes.SGM_MULTI_SELECTIONS_LIABILITY_EXCEEDED) {
					return 'SGM selection has exceeded multi liability limit';
				}
			},
			text(alert) {
				let text = '';
				if (alert.source instanceof Race) {
					text = `A ${alert.source.race_type.name.toLowerCase()} event "${alert.source.meeting.name} - ${
						alert.source.name
					}" that started at "${dayjs(alert.source.start_time).format('D MMM YYYY HH:mm:ss Z')}" `;
				} else if (alert.source instanceof Meeting) {
					text = `A ${alert.source.race_type.name.toLowerCase()} meeting "${
						alert.source.name
					}" that started at "${dayjs(alert.source.start_time).format('D MMM YYYY HH:mm:ss Z')}" `;
				}

				if (alert.alert_type.id === AlertTypes.RESULT_MISMATCH) {
					text = text + 'has received conflicting results and needs to be addressed!';
				} else if (alert.alert_type.id === AlertTypes.NOT_FINALISED) {
					text = text + 'has not been finalised!';
				}

				if (
					alert.alert_type.id === AlertTypes.SELECTION_LIABILITY_EXCEEDED ||
					alert.alert_type.id === AlertTypes.SGM_SELECTION_LIABILITY_EXCEEDED
				) {
					if (alert.source instanceof Selection) {
						return (
							`${alert.source.event.race_type.name}. ` +
							`${alert.source.event.name}. ` +
							`Race ${alert.source.event.race_number}. ` +
							`No ${alert.source.number} - ${alert.source.name} ` +
							'has exceeded selection liability limit'
						);
					} else if (alert.source instanceof SportSelection) {
						return (
							`${alert.source.event.sport.name}. ` +
							`${alert.source.event.competition.name}. ` +
							`${alert.source.event.name}. ` +
							`${alert.source.market.name}. ` +
							`${alert.source.name} ` +
							'has exceeded selection liability limit'
						);
					} else if (alert.source instanceof SportcastSelection) {
						return (
							`${alert.source.sport_event.sport.name}. ` +
							`${alert.source.sport_event.competition.name}. ` +
							`${alert.source.sport_event.name}. ` +
							`${alert.source.name} ` +
							'has exceeded SGM selection liability limit'
						);
					}
				}

				if (
					alert.alert_type.id === AlertTypes.MULTI_SELECTIONS_LIABILITY_EXCEEDED ||
					alert.alert_type.id === AlertTypes.SGM_MULTI_SELECTIONS_LIABILITY_EXCEEDED
				) {
					if (alert.source instanceof Selection) {
						return (
							`${alert.source.event.race_type.name}. ` +
							`${alert.source.event.name}. ` +
							`Race ${alert.source.event.race_number}. ` +
							`No ${alert.source.number} - ${alert.source.name} ` +
							'has exceeded selection multi liability limit'
						);
					} else if (alert.source instanceof SportSelection) {
						return (
							`${alert.source.event.sport.name}. ` +
							`${alert.source.event.competition.name}. ` +
							`${alert.source.event.name}. ` +
							`${alert.source.market.name}. ` +
							`${alert.source.name} ` +
							'has exceeded selection multi liability limit'
						);
					} else if (alert.source instanceof SportcastSelection) {
						return (
							`${alert.source.sport_event.sport.name}. ` +
							`${alert.source.sport_event.competition.name}. ` +
							`${alert.source.sport_event.name}. ` +
							`${alert.source.name} ` +
							'has exceeded SGM selection multi liability limit'
						);
					}
				}

				return text;
			},
			icon(alert) {
				if (alert.source instanceof Race || alert.source instanceof Meeting) {
					return alert.source.race_type.name.toLowerCase();
				}
				if (alert.source instanceof Selection) {
					return alert.source.event.race_type.name.toLowerCase();
				}
				if (alert.source instanceof SportSelection) {
					return alert.source.event.sport.icon !== null
						? alert.source.event.sport.icon
						: getIconForSport(alert.source.event.sport.name.toString());
				}
				if (alert.source instanceof SportcastSelection) {
					return alert.source.sport_event.sport.icon !== null
						? alert.source.sport_event.sport.icon
						: getIconForSport(alert.source.sport_event.sport.name.toString());
				}
			},
			category(alert) {
				if (alert.source instanceof Race || alert.source instanceof Meeting || alert.source instanceof Selection) {
					return BetCategory.Racing;
				} else if (
					alert.source instanceof Sport ||
					alert.source instanceof Competition ||
					alert.source instanceof SportSelection ||
					alert.source instanceof SportcastSelection
				) {
					return BetCategory.Sport;
				}
			},
			level(alert) {
				return alert.getCurrentAlertLevel();
			},
			permission(alert) {
				if (alert.source instanceof Race) {
					return 'races.update';
				} else if (alert.source instanceof Sport) {
					return 'sport_events.update';
				} else if (alert.source instanceof Meeting) {
					return 'meetings.update';
				} else if (alert.source instanceof Selection) {
					return 'races.update';
				} else if (alert.source instanceof SportSelection) {
					return 'sport_events.update';
				} else if (alert.source instanceof SportcastSelection) {
					return 'sport_events.update';
				}
			},
			link(alert) {
				if (alert.source instanceof Race) {
					return routeForMeetingRace(alert.source.meeting, alert.source);
				} else if (alert.source instanceof Meeting) {
					return routeForMeeting(alert.source);
				} else if (alert.source instanceof Selection) {
					return routeForMeetingRace(alert.source.event.meeting, alert.source.event);
				} else if (alert.source instanceof Sport) {
					// haven't had any of these alert to know the source payload
				} else if (alert.source instanceof SportSelection) {
					return routeForSportEvent(alert.source.event);
				} else if (alert.source instanceof SportcastSelection) {
					return routeForSportEvent(alert.source.sport_event);
				}
			},
			actions(alert) {
				let actions = [];
				if (alert.alert_type.id === AlertTypes.RESULT_MISMATCH) {
					if (alert.source instanceof Race || alert.source instanceof Meeting) {
						actions = [];
					}
				} else if (alert.alert_type.id === AlertTypes.NOT_FINALISED) {
					if (alert.source instanceof Race || alert.source instanceof Meeting) {
						actions = [
							{
								text: 'Finalise event',
								action: 'navigate',
								actionData:
									alert.source instanceof Meeting
										? routeForMeeting(alert.source)
										: routeForMeetingRace(alert.source.meeting, alert.source)
							}
						];
					}
				} else if (
					alert.alert_type.id === AlertTypes.SELECTION_LIABILITY_EXCEEDED ||
					alert.alert_type.id === AlertTypes.MULTI_SELECTIONS_LIABILITY_EXCEEDED ||
					alert.alert_type.id === AlertTypes.SGM_SELECTION_LIABILITY_EXCEEDED ||
					alert.alert_type.id === AlertTypes.SGM_MULTI_SELECTIONS_LIABILITY_EXCEEDED
				) {
					if (alert.source instanceof Selection) {
						actions = [];
					} else if (alert.source instanceof SportSelection) {
						actions = [];
					}
				}

				if (ACLStore.granted('alerts.delete')) {
					actions.push({
						text: 'Dismiss',
						action: 'dismiss',
						actionData: {
							id: alert.id
						}
					});
				}
				return actions;
			}
		};
	}
}
